// Components
import { Button, addToast } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Card, Col, Row } from 'reactstrap';

import { updateFinantialConfig } from '../../../api/requests/settings';
import useUserState from '../../../hooks/useUserState';
import { TranslationsKeys } from '../../../locales/translations';
import { PermissionName } from '../../../types/Auth';
import { FinantialSettingsContext } from './contexts/finantial-settings.context';
// Hooks
import useFinantialSettings from './hooks/useFinantialSettings';
// Types
import { FinantialConfig } from './interfaces/finantial-config.interface';
import EquivalenceCreditSection from './parts/EquivalenceCreditSection';
import PaydaySection from './parts/PaydaySection';
import TuitionPaymentSection from './parts/TuitionPaymentSection';
import AdditionalCharges from '../AdditionalCharges';
import TuitionPaymentTypeSection from './parts/TuitionPaymentTypeSection';
// Utils
import {
  DEFAULT_VALUES,
  parseDefaultValuesFromFetch,
  parseDefaultValuesToFetch,
} from './utils/parse-default-values';

// Render
export default function Finantial() {
  const { t } = useTranslation(TranslationsKeys.CONFIGURATION_FINANTIAL);
  const { isAuthorizedTo } = useUserState();

  const {
    config: fetchedConfigs,
    formData,
    isLoading,
    isRefetching,
  } = useFinantialSettings();

  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const methods = useForm<FinantialConfig>({
    defaultValues: DEFAULT_VALUES,
  });

  const { reset, handleSubmit } = methods;

  const showSave = useMemo(() => {
    return !isLoading && !!isAuthorizedTo([PermissionName.SETTINGS]);
  }, [isAuthorizedTo, isLoading]);

  useEffect(() => {
    if (fetchedConfigs) {
      reset(parseDefaultValuesFromFetch(fetchedConfigs));
    }
  }, [fetchedConfigs, isRefetching, reset]);

  const handleSave = useCallback(
    async (values: FinantialConfig) => {
      if (isUpdating || isLoading) {
        return;
      }
      // Los campos de intereses, cuotas y abonos ya no se editan acá (viven en
      // Portal Financiero > Configuración): el payload ni siquiera los envía,
      // porque el backend (`saveGeneralPaymentOptions`) los ignora igual.
      const payload = parseDefaultValuesToFetch(values);

      setIsUpdating(true);

      const { error } = await updateFinantialConfig(payload);
      setIsUpdating(false);

      if (error) {
        addToast({
          icon: 'error',
          text: t(`toasts.errorSavingChanges`),
          color: 'danger',
        });
      } else {
        addToast({
          icon: 'success',
          text: t(`toasts.changesSaved`),
          color: 'success',
        });
      }
    },
    [t, isUpdating, isLoading],
  );

  return (
    <FinantialSettingsContext.Provider
      value={{
        loading: isLoading,
        disabled: isUpdating || isLoading || !showSave,
        formData: formData || undefined,
      }}
    >
      <FormProvider {...methods}>
        <form className="w-100" onSubmit={handleSubmit(handleSave)}>
          <Card className="p-4">
            <Row>
              <Col sm={12}>
                <p className="fs-14 text-medium mb-3">{t(`description`)}</p>
              </Col>
              <Col sm={12}>
                <TuitionPaymentTypeSection />
              </Col>
              <Col sm={12}>
                <PaydaySection />
              </Col>
              <Col sm={12}>
                <TuitionPaymentSection />
              </Col>
              <Col sm={12}>
                <EquivalenceCreditSection />
              </Col>
              <Col sm={12} className="mt-4">
                <AdditionalCharges />
              </Col>
              <Col sm={12}>
                {showSave && (
                  <Button
                    type="submit"
                    text={t(`actions.save`)}
                    size="md"
                    className="float-right mt-5"
                    disabled={isRefetching || isUpdating}
                    loading={isUpdating}
                  />
                )}
              </Col>
            </Row>
          </Card>
        </form>
      </FormProvider>
    </FinantialSettingsContext.Provider>
  );
}
